import { round } from '@/utils/mathUtils';
import type { MutableRefObject } from 'react';
import { memo, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type {
  IItemData,
  IItemDataResponse,
  IItemDataCount,
  ISuppliars,
} from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';
import { ItemsSelect } from '@/pages/PurchasesContainer/RequestPerHand/components/SelectItems';

interface ItemSelectionSectionProps {
  items: IItemData[] | undefined;
  selectedItem: IItemData | null;
  itemData: IItemDataResponse | null;
  suppliar: ISuppliars | null;
  selectItemRef: MutableRefObject<HTMLInputElement | null>;
  isPending: boolean;
  handleSelectItem: (item: IItemData | null) => void;
  fnToGoToQty: () => void;
}

// Loading spinner component to avoid repetition
const LoadingSpinner = () => (
  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
);

// Memoized item list component
const ItemsList = memo(
  ({
    items,
    sortedBy = 'price',
  }: {
    items: IItemDataCount[];
    sortedBy?: 'price' | 'name';
  }) => {
    // Sort items by price (high to low) by default
    const sortedItems = useMemo(() => {
      return [...items].sort((a: IItemDataCount, b: IItemDataCount) =>
        sortedBy === 'price'
          ? (b.Price || 0) - (a.Price || 0)
          : a.Name?.localeCompare(b.Name || '') || 0,
      );
    }, [items, sortedBy]);

    return (
      <div className="divide-y dark:divide-gray-700">
        {sortedItems.map((subItem: IItemDataCount, index: number) => {
          const hasCount =
            subItem.Store1Count > 0 ||
            subItem.Store2Count > 0 ||
            subItem.StoreOtherCount > 0;

          return (
            <div
              key={index}
              className={`grid grid-cols-[200px_1fr_1fr_1fr_1fr] gap-1 px-3 py-2 text-sm ${
                hasCount
                  ? 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-300'
                  : 'dark:bg-gray-900'
              }`}
            >
              <div className="min-w-[120px] break-words">{subItem.Name}</div>
              <div className="text-center font-medium">
                {subItem.Price
                  ? round(subItem.Price, 2).toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })
                  : '-'}
              </div>
              <div className="text-center">{subItem.Store1Count || '-'}</div>
              <div className="text-center">{subItem.Store2Count || '-'}</div>
              <div className="text-center">
                {subItem.StoreOtherCount || '-'}
              </div>
            </div>
          );
        })}
      </div>
    );
  },
);

ItemsList.displayName = 'ItemsList';

// Item count display with loading state
const CountDisplay = memo(
  ({
    count,
    isPending,
    selectedItem,
  }: {
    count: number;
    isPending: boolean;
    selectedItem: IItemData | null;
  }) => (
    <div className="flex h-10 w-full items-center rounded-md border border-input bg-gray-200 px-3 py-1 text-lg text-gray-700 dark:bg-gray-800 dark:text-gray-300">
      {isPending ? <LoadingSpinner /> : selectedItem ? count : '-'}
    </div>
  ),
);

CountDisplay.displayName = 'CountDisplay';

export const ItemSelectionSection = memo(
  ({
    items,
    selectedItem,
    itemData,
    suppliar,
    selectItemRef,
    isPending,
    handleSelectItem,
    fnToGoToQty,
  }: ItemSelectionSectionProps) => {
    // Memoize derived values to avoid recalculations on each render
    const {
      countRetail,
      countStore2,
      countStoreOther,
      countOtherPrice,
      groupItemsCount,
      hasMultipleItems,
    } = useMemo(() => {
      const mainItemData = itemData?.AllItemsData?.find((x) => x?.IsMain);
      const countRetail = mainItemData?.Store1Count ?? 0;
      const countStore2 = mainItemData?.Store2Count ?? 0;
      const countStoreOther = mainItemData?.StoreOtherCount ?? 0;
      const countOtherPrice = itemData?.OtherItemDataCount ?? 0;
      const groupItemsCount = itemData?.AllItemsData?.length ?? 0;
      const hasMultipleItems = groupItemsCount > 1;

      return {
        countRetail,
        countStore2,
        countStoreOther,
        countOtherPrice,
        groupItemsCount,
        hasMultipleItems,
      };
    }, [itemData]);

    // Memoize the popover content to avoid recreation
    const popoverContent = useMemo(() => {
      if (!itemData?.AllItemsData || itemData.AllItemsData.length === 0) {
        return null;
      }

      return (
        <div className="space-y-2">
          <div className="mt-2">
            <div className="mb-2 font-medium">تفاصيل الأصناف:</div>
            <div className="rounded-md border dark:border-gray-700">
              <div className="grid grid-cols-[200px_1fr_1fr_1fr_1fr] gap-1 border-b bg-gray-50 px-3 py-2 text-xs font-medium dark:border-gray-700 dark:bg-gray-800">
                <div className="">الصنف</div>
                <div className="text-center">السعر</div>
                <div className="text-center">قطاعي</div>
                <div className="text-center">جملة</div>
                <div className="text-center">باقي </div>
              </div>
              {itemData.AllItemsData && (
                <ItemsList items={itemData.AllItemsData} />
              )}
            </div>
          </div>
        </div>
      );
    }, [itemData?.AllItemsData]);

    return (
      <div className="flex flex-col items-center gap-2 border-b p-2">
        <div className="relative w-full flex-1">
          <ItemsSelect
            options={items}
            handleSelectItems={handleSelectItem}
            selectedItem={selectedItem}
            selectItemRef={selectItemRef}
            fnToGoToQtyRef={fnToGoToQty}
            disabled={!suppliar}
          />
          <div className="mt-1 flex w-full items-center gap-2">
            {hasMultipleItems && (
              <Badge
                variant="outline"
                className="flex-grow rounded-md border border-green-300 bg-green-50 px-2 py-1 text-center text-sm font-medium text-green-700 shadow-sm dark:border-green-800 dark:bg-green-950/40 dark:text-green-300"
              >
                مجموعة من {groupItemsCount} اسعار
              </Badge>
            )}
            <div className="ml-auto flex items-center gap-1 rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 shadow-sm dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300">
              <kbd className="rounded-sm bg-white px-1 font-sans shadow-sm dark:bg-gray-800">
                Ctrl
              </kbd>
              <span>+</span>
              <kbd className="rounded-sm bg-white px-1 font-sans shadow-sm dark:bg-gray-800">
                Alt
              </kbd>
              <span>+</span>
              <kbd className="rounded-sm bg-white px-1 font-sans shadow-sm dark:bg-gray-800">
                ↑
              </kbd>
            </div>
          </div>
        </div>
        <div className="grid w-full grid-cols-4 gap-1">
          <div className="flex w-full flex-col gap-1">
            <Label className="text-center text-xs">رصيد قطاعي </Label>
            <CountDisplay
              count={countRetail}
              isPending={isPending}
              selectedItem={selectedItem}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-center text-xs">رصيد جملة</Label>
            <CountDisplay
              count={countStore2}
              isPending={isPending}
              selectedItem={selectedItem}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-center text-xs">رصيد مخازن</Label>
            <CountDisplay
              count={countStoreOther}
              isPending={isPending}
              selectedItem={selectedItem}
            />
          </div>
          <div className="flex min-w-20 flex-col gap-1">
            <Label className="text-center text-xs"> اسعار اخري</Label>
            <Popover>
              <PopoverTrigger asChild>
                <div className="flex h-10 w-full cursor-help items-center rounded-md border border-input bg-gray-200 px-3 py-2 text-lg text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {isPending ? (
                    <LoadingSpinner />
                  ) : selectedItem ? (
                    countOtherPrice || '-'
                  ) : (
                    '-'
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-[450px]">
                {popoverContent}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    );
  },
);

ItemSelectionSection.displayName = 'ItemSelectionSection';
